"use client";

import { useParams, usePathname as useNextPathname } from "next/navigation";
import config from "@/config.json";

/**
 * Hook для получения текущей локали из URL
 * Использует params для определения локали из пути
 */
export function useLocale(): string {
  const params = useParams<{ locale?: string }>();
  const fullPathname = useNextPathname();
  
  const languages = config.websiteLanguages as string[];
  const defaultLocale = config.defaultWebsiteLanguage || languages[0] || "en";
  
  // Пытаемся получить локаль из params (самый надежный способ в App Router)
  if (params?.locale && languages.includes(params.locale)) {
    return params.locale;
  }
  
  // Если не нашли в params, пробуем из полного pathname
  if (fullPathname) {
    const pathParts = fullPathname.split("/").filter(Boolean);
    const firstPart = pathParts[0];
    
    if (firstPart && languages.includes(firstPart)) {
      return firstPart;
    }
  }
  
  return defaultLocale;
}

/**
 * Функция для формирования локализованного пути
 * Принимает путь без локали (например "/articles" или "/news/about-us")
 * и возвращает путь с текущей локалью (например "/he/articles" или "/he/news/about-us")
 * 
 * @param path - путь без локали (начинается с /)
 * @param locale - локаль (если не указана, используется текущая из useLocale)
 * @returns локализованный путь
 */
export function getLocalizedPath(path: string, locale?: string): string {
  // Убираем ведущий слэш если есть
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  
  // Если путь уже содержит локаль в начале, убираем его
  const languages = config.websiteLanguages as string[];
  const pathParts = cleanPath.split("/");
  
  if (pathParts.length > 0 && languages.includes(pathParts[0])) {
    // Убираем локаль из пути
    pathParts.shift();
    const pathWithoutLocale = pathParts.join("/");
    const finalLocale = locale || "en";
    return `/${finalLocale}${pathWithoutLocale ? `/${pathWithoutLocale}` : ""}`;
  }
  
  // Добавляем локаль
  const finalLocale = locale || "en";
  return `/${finalLocale}${cleanPath ? `/${cleanPath}` : ""}`;
}

/**
 * Синхронная функция для получения локали из pathname (для использования вне компонентов)
 */
export function getLocaleFromPathname(pathname: string | null): string {
  if (!pathname) {
    return config.defaultWebsiteLanguage || (config.websiteLanguages as string[])[0] || "en";
  }
  
  const languages = config.websiteLanguages as string[];
  const pathParts = pathname.split("/").filter(Boolean);
  const firstPart = pathParts[0];
  
  if (firstPart && languages.includes(firstPart)) {
    return firstPart;
  }
  
  return config.defaultWebsiteLanguage || languages[0] || "en";
}

